import React from 'react';
import { Button, Input, Label, FormGroup, Row, Col } from 'reactstrap';
import Swal from 'sweetalert2';
import {
  AlignLeft, AlignCenter, AlignRight, Bold, Italic, Underline, Trash2, Copy,
  ChevronsUp, ChevronsDown, ChevronUp, ChevronDown, Lock, Unlock, Eye, EyeOff,
} from 'react-feather';
import { useAppDispatch, useAppSelector } from '../../../../../ReaduxToolkit/Hooks';
import { updateElement, updateElements, removeElements, duplicateElements, reorderElement, groupElements, ungroupElements } from '../store/formDocumentSlice';
import { setSelection, setActiveCell } from '../store/formDesignerUiSlice';
import { runsToText, textToRuns } from '../contentRuns';
import { FormElement, TableProps } from '../store/types';
import { findPageIdForSelection } from '../pageUtils';
import { mergeRight, mergeDown, unmergeCell, setCellContent, isCoveredCell, tableHeight } from '../tableUtils';

const CONTENT_TYPES = new Set(['text', 'label', 'paragraph', 'heading', 'pagenumber']);
const BINDABLE_TYPES = new Set(['textbox', 'textarea', 'date', 'text', 'label', 'paragraph']);

export default function PropertiesPanel() {
  const dispatch = useAppDispatch();
  const doc = useAppSelector((s) => s.FormDocument.present);
  const selectedIds = useAppSelector((s) => s.FormDesignerUi.selectedIds);
  const activeCell = useAppSelector((s) => s.FormDesignerUi.activeCell);
  const candidatePageId = findPageIdForSelection(doc, selectedIds);
  const page = doc.pages.find((p) => p.id === candidatePageId);

  if (!page) return null;
  const pageId = page.id;
  const selected = page.elements.filter((e) => selectedIds.includes(e.id));

  if (selected.length === 0) {
    return (
      <div className="fd-properties-panel fd-properties-empty">
        <p className="text-muted">Select an element to edit its properties.</p>
      </div>
    );
  }

  const layerButtons = (
    <div className="fd-properties-section">
      <div className="fd-properties-section-title">Layer</div>
      <div className="fd-btn-row">
        <Button size="sm" color="light" title="Bring to front" onClick={() => selectedIds.forEach((id) => dispatch(reorderElement({ pageId, id, direction: 'front' })))}>
          <ChevronsUp size={14} />
        </Button>
        <Button size="sm" color="light" title="Bring forward" onClick={() => selectedIds.forEach((id) => dispatch(reorderElement({ pageId, id, direction: 'forward' })))}>
          <ChevronUp size={14} />
        </Button>
        <Button size="sm" color="light" title="Send backward" onClick={() => selectedIds.forEach((id) => dispatch(reorderElement({ pageId, id, direction: 'backward' })))}>
          <ChevronDown size={14} />
        </Button>
        <Button size="sm" color="light" title="Send to back" onClick={() => selectedIds.forEach((id) => dispatch(reorderElement({ pageId, id, direction: 'back' })))}>
          <ChevronsDown size={14} />
        </Button>
      </div>
      <div className="fd-btn-row mt-1">
        <Button size="sm" color="light" title="Duplicate" onClick={() => dispatch(duplicateElements({ pageId, ids: selectedIds }))}>
          <Copy size={14} />
        </Button>
        <Button
          size="sm"
          color="light"
          title={selected.every((e) => e.locked) ? 'Unlock' : 'Lock'}
          onClick={() => {
            const shouldLock = !selected.every((e) => e.locked);
            dispatch(updateElements({ pageId, changes: selectedIds.map((id) => ({ id, changes: { locked: shouldLock } })) }));
          }}
        >
          {selected.every((e) => e.locked) ? <Unlock size={14} /> : <Lock size={14} />}
        </Button>
        <Button
          size="sm"
          color="light"
          title={selected.every((e) => !e.visible) ? 'Show' : 'Hide'}
          onClick={() => {
            const shouldShow = selected.every((e) => !e.visible);
            dispatch(updateElements({ pageId, changes: selectedIds.map((id) => ({ id, changes: { visible: shouldShow } })) }));
          }}
        >
          {selected.every((e) => !e.visible) ? <Eye size={14} /> : <EyeOff size={14} />}
        </Button>
        <Button
          size="sm"
          color="danger"
          outline
          title="Delete"
          onClick={() => {
            dispatch(removeElements({ pageId, ids: selectedIds }));
            dispatch(setSelection([]));
          }}
        >
          <Trash2 size={14} />
        </Button>
      </div>
      {selected.length > 1 && (
        <div className="fd-btn-row mt-1">
          <Button size="sm" color="light" onClick={() => dispatch(groupElements({ pageId, ids: selectedIds }))}>
            Group
          </Button>
          {selected[0].groupId && (
            <Button size="sm" color="light" onClick={() => dispatch(ungroupElements({ pageId, groupId: selected[0].groupId as string }))}>
              Ungroup
            </Button>
          )}
        </div>
      )}
    </div>
  );

  if (selected.length > 1) {
    const minX = Math.min(...selected.map((e) => e.x));
    const maxRight = Math.max(...selected.map((e) => e.x + e.width));
    const minY = Math.min(...selected.map((e) => e.y));
    const maxBottom = Math.max(...selected.map((e) => e.y + e.height));

    const align = (fn: (e: FormElement) => Partial<FormElement>) => {
      dispatch(updateElements({ pageId, changes: selected.map((e) => ({ id: e.id, changes: fn(e) })) }));
    };

    return (
      <div className="fd-properties-panel">
        <div className="fd-properties-section">
          <div className="fd-properties-section-title">{selected.length} elements selected</div>
          <div className="fd-btn-row">
            <Button size="sm" color="light" title="Align left" onClick={() => align(() => ({ x: minX }))}><AlignLeft size={14} /></Button>
            <Button size="sm" color="light" title="Align center" onClick={() => align((e) => ({ x: (minX + maxRight) / 2 - e.width / 2 }))}><AlignCenter size={14} /></Button>
            <Button size="sm" color="light" title="Align right" onClick={() => align((e) => ({ x: maxRight - e.width }))}><AlignRight size={14} /></Button>
          </div>
          <div className="fd-btn-row mt-1">
            <Button size="sm" color="light" onClick={() => align(() => ({ y: minY }))}>Top</Button>
            <Button size="sm" color="light" onClick={() => align((e) => ({ y: (minY + maxBottom) / 2 - e.height / 2 }))}>Middle</Button>
            <Button size="sm" color="light" onClick={() => align((e) => ({ y: maxBottom - e.height }))}>Bottom</Button>
          </div>
        </div>
        {layerButtons}
      </div>
    );
  }

  const el = selected[0];
  const update = (changes: Partial<FormElement>) => dispatch(updateElement({ pageId, id: el.id, changes }));
  const updateStyle = (changes: Partial<FormElement['style']>) => update({ style: { ...el.style, ...changes } });
  const updateTypeProps = (changes: Partial<FormElement['typeProps']>) => update({ typeProps: { ...el.typeProps, ...changes } });
  const updateBindings = (changes: Partial<NonNullable<FormElement['bindings']>>) => update({ bindings: { ...el.bindings, ...changes } });

  // The table's own FormElement.height must track the sum of row heights, or extra rows
  // get clipped by the element wrapper's overflow:hidden instead of actually appearing.
  const applyTable = (t: TableProps) => update({ height: tableHeight(t), typeProps: { ...el.typeProps, table: t } });

  const addTableRow = () => {
    const t = el.typeProps?.table!;
    const rows = t.rows + 1;
    const cells = [...t.cells, Array.from({ length: t.cols }, () => [])];
    const rowHeights = [...t.rowHeights, 40];
    applyTable({ ...t, rows, cells, rowHeights });
  };

  const removeTableRow = () => {
    const t = el.typeProps?.table!;
    if (t.rows <= 1) return;
    const rows = t.rows - 1;
    const cells = t.cells.slice(0, rows);
    const rowHeights = t.rowHeights.slice(0, rows);
    const merges = t.merges.filter((m) => m.r + m.rowSpan <= rows);
    applyTable({ ...t, rows, cells, rowHeights, merges });
  };

  const addTableColumn = () => {
    const t = el.typeProps?.table!;
    const cols = t.cols + 1;
    const cells = t.cells.map((row) => [...row, []]);
    const colWidths = Array.from({ length: cols }, () => 100 / cols);
    applyTable({ ...t, cols, cells, colWidths, merges: [] });
  };

  const removeTableColumn = () => {
    const t = el.typeProps?.table!;
    if (t.cols <= 1) return;
    const cols = t.cols - 1;
    const cells = t.cells.map((row) => row.slice(0, cols));
    const colWidths = Array.from({ length: cols }, () => 100 / cols);
    applyTable({ ...t, cols, cells, colWidths, merges: [] });
  };

  const activeTableCell = el.type === 'table' && activeCell && activeCell.elementId === el.id ? activeCell : null;

  // Advance to the next (or, with Shift, previous) non-covered cell in row-major order.
  // Tab from the very last cell grows the table by one row instead of doing nothing.
  const handleCellTab = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !activeTableCell) return;
    e.preventDefault();
    const t = el.typeProps?.table!;
    const { row, col } = activeTableCell;
    const step = e.shiftKey ? -1 : 1;

    if (!e.shiftKey && row === t.rows - 1 && col === t.cols - 1) {
      const rows = t.rows + 1;
      const cells = [...t.cells, Array.from({ length: t.cols }, () => [])];
      const rowHeights = [...t.rowHeights, 40];
      applyTable({ ...t, rows, cells, rowHeights });
      dispatch(setActiveCell({ elementId: el.id, row: rows - 1, col: 0 }));
      return;
    }

    let r = row;
    let c = col + step;
    while (r >= 0 && r < t.rows) {
      if (c < 0) {
        r -= 1;
        c = t.cols - 1;
        continue;
      }
      if (c >= t.cols) {
        r += 1;
        c = 0;
        continue;
      }
      if (!isCoveredCell(t, r, c)) {
        dispatch(setActiveCell({ elementId: el.id, row: r, col: c }));
        return;
      }
      c += step;
    }
  };

  const cellEditor = !activeTableCell ? (
    <p className="text-muted small mb-0">Click a cell on the canvas to edit its text or merge it with a neighbor.</p>
  ) : (
    <>
      <Label size="sm" className="mt-2">
        Cell ({activeTableCell.row + 1}, {activeTableCell.col + 1}) content
      </Label>
      <Input
        type="textarea"
        bsSize="sm"
        rows={2}
        value={runsToText(el.typeProps?.table?.cells?.[activeTableCell.row]?.[activeTableCell.col])}
        onChange={(e) => {
          const t = el.typeProps?.table!;
          updateTypeProps({ table: setCellContent(t, activeTableCell.row, activeTableCell.col, textToRuns(e.target.value)) });
        }}
        onKeyDown={handleCellTab}
        placeholder="Use <<Field Name>> to insert a dynamic variable (Tab moves to the next cell; Tab in the last cell adds a row)"
      />
      <div className="fd-btn-row mt-1">
        <Button
          size="sm"
          color="light"
          onClick={() => applyTable(mergeRight(el.typeProps?.table!, activeTableCell.row, activeTableCell.col))}
        >
          Merge Right
        </Button>
        <Button
          size="sm"
          color="light"
          onClick={() => applyTable(mergeDown(el.typeProps?.table!, activeTableCell.row, activeTableCell.col))}
        >
          Merge Down
        </Button>
        <Button
          size="sm"
          color="light"
          outline
          onClick={() => applyTable(unmergeCell(el.typeProps?.table!, activeTableCell.row, activeTableCell.col))}
        >
          Unmerge
        </Button>
      </div>
    </>
  );

  return (
    <div className="fd-properties-panel">
      {layerButtons}
      <div className="fd-properties-section">
        <div className="fd-properties-section-title">Position &amp; Size</div>
        <Row>
          <Col xs={6}><FormGroup><Label size="sm">X</Label><Input type="number" bsSize="sm" value={Math.round(el.x)} onChange={(e) => update({ x: Number(e.target.value) })} /></FormGroup></Col>
          <Col xs={6}><FormGroup><Label size="sm">Y</Label><Input type="number" bsSize="sm" value={Math.round(el.y)} onChange={(e) => update({ y: Number(e.target.value) })} /></FormGroup></Col>
          <Col xs={6}><FormGroup><Label size="sm">Width</Label><Input type="number" bsSize="sm" value={Math.round(el.width)} onChange={(e) => update({ width: Number(e.target.value) })} /></FormGroup></Col>
          <Col xs={6}><FormGroup><Label size="sm">Height</Label><Input type="number" bsSize="sm" value={Math.round(el.height)} onChange={(e) => update({ height: Number(e.target.value) })} /></FormGroup></Col>
          <Col xs={12}><FormGroup><Label size="sm">Rotation</Label><Input type="number" bsSize="sm" value={Math.round(el.rotation)} onChange={(e) => update({ rotation: Number(e.target.value) })} /></FormGroup></Col>
        </Row>
      </div>

      {CONTENT_TYPES.has(el.type) && (
        <div className="fd-properties-section">
          <div className="fd-properties-section-title">Content</div>
          <FormGroup>
            <Input
              type="textarea"
              bsSize="sm"
              rows={3}
              value={runsToText(el.content)}
              onChange={(e) => update({ content: textToRuns(e.target.value) })}
              placeholder="Use <<Field Name>> to insert a dynamic variable"
            />
          </FormGroup>
        </div>
      )}

      <div className="fd-properties-section">
        <div className="fd-properties-section-title">Text Style</div>
        <div className="fd-btn-row">
          <Button size="sm" color={el.style.fontWeight === 'bold' ? 'primary' : 'light'} onClick={() => updateStyle({ fontWeight: el.style.fontWeight === 'bold' ? 'normal' : 'bold' })}><Bold size={14} /></Button>
          <Button size="sm" color={el.style.fontStyle === 'italic' ? 'primary' : 'light'} onClick={() => updateStyle({ fontStyle: el.style.fontStyle === 'italic' ? 'normal' : 'italic' })}><Italic size={14} /></Button>
          <Button size="sm" color={el.style.textDecoration === 'underline' ? 'primary' : 'light'} onClick={() => updateStyle({ textDecoration: el.style.textDecoration === 'underline' ? 'none' : 'underline' })}><Underline size={14} /></Button>
          <Button size="sm" color={el.style.textAlign === 'left' ? 'primary' : 'light'} onClick={() => updateStyle({ textAlign: 'left' })}><AlignLeft size={14} /></Button>
          <Button size="sm" color={el.style.textAlign === 'center' ? 'primary' : 'light'} onClick={() => updateStyle({ textAlign: 'center' })}><AlignCenter size={14} /></Button>
          <Button size="sm" color={el.style.textAlign === 'right' ? 'primary' : 'light'} onClick={() => updateStyle({ textAlign: 'right' })}><AlignRight size={14} /></Button>
        </div>
        <Row className="mt-1">
          <Col xs={7}>
            <FormGroup>
              <Label size="sm">Font size</Label>
              <Input type="number" bsSize="sm" value={el.style.fontSize} onChange={(e) => updateStyle({ fontSize: Number(e.target.value) })} />
            </FormGroup>
          </Col>
          <Col xs={5}>
            <FormGroup>
              <Label size="sm">Color</Label>
              <Input type="color" bsSize="sm" value={el.style.color} onChange={(e) => updateStyle({ color: e.target.value })} />
            </FormGroup>
          </Col>
        </Row>
      </div>

      <div className="fd-properties-section">
        <div className="fd-properties-section-title">Appearance</div>
        <Row>
          <Col xs={6}>
            <FormGroup>
              <Label size="sm">Background</Label>
              <Input type="color" bsSize="sm" value={el.style.background === 'transparent' ? '#ffffff' : el.style.background} onChange={(e) => updateStyle({ background: e.target.value })} />
            </FormGroup>
          </Col>
          <Col xs={6}>
            <FormGroup>
              <Label size="sm">Border color</Label>
              <Input type="color" bsSize="sm" value={el.style.borderColor} onChange={(e) => updateStyle({ borderColor: e.target.value })} />
            </FormGroup>
          </Col>
          <Col xs={6}>
            <FormGroup>
              <Label size="sm">Border width</Label>
              <Input type="number" bsSize="sm" value={el.style.borderWidth} onChange={(e) => updateStyle({ borderWidth: Number(e.target.value) })} />
            </FormGroup>
          </Col>
          <Col xs={6}>
            <FormGroup>
              <Label size="sm">Border style</Label>
              <Input type="select" bsSize="sm" value={el.style.borderStyle} onChange={(e) => updateStyle({ borderStyle: e.target.value as any })}>
                <option value="none">None</option>
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>
      </div>

      {el.type === 'checkbox' && (
        <div className="fd-properties-section">
          <div className="fd-properties-section-title">Checkbox</div>
          <FormGroup>
            <Label size="sm">Label</Label>
            <Input bsSize="sm" value={el.typeProps?.checkbox?.label || ''} onChange={(e) => updateTypeProps({ checkbox: { ...el.typeProps?.checkbox!, label: e.target.value } })} />
          </FormGroup>
          <FormGroup check>
            <Label check>
              <Input type="checkbox" checked={!!el.typeProps?.checkbox?.checked} onChange={(e) => updateTypeProps({ checkbox: { ...el.typeProps?.checkbox!, checked: e.target.checked } })} />
              {' '}Checked by default
            </Label>
          </FormGroup>
        </div>
      )}

      {el.type === 'radio' && (
        <div className="fd-properties-section">
          <div className="fd-properties-section-title">Radio</div>
          <FormGroup>
            <Label size="sm">Label</Label>
            <Input bsSize="sm" value={el.typeProps?.radio?.label || ''} onChange={(e) => updateTypeProps({ radio: { ...el.typeProps?.radio!, label: e.target.value } })} />
          </FormGroup>
          <FormGroup>
            <Label size="sm">Group name</Label>
            <Input bsSize="sm" value={el.typeProps?.radio?.groupName || ''} onChange={(e) => updateTypeProps({ radio: { ...el.typeProps?.radio!, groupName: e.target.value } })} />
          </FormGroup>
        </div>
      )}

      {el.type === 'dropdown' && (
        <div className="fd-properties-section">
          <div className="fd-properties-section-title">Dropdown Options</div>
          <FormGroup>
            <Input
              type="textarea"
              bsSize="sm"
              rows={3}
              value={(el.typeProps?.dropdown?.options || []).join('\n')}
              onChange={(e) => updateTypeProps({ dropdown: { options: e.target.value.split('\n') } })}
              placeholder="One option per line"
            />
          </FormGroup>
        </div>
      )}

      {el.type === 'table' && (
        <div className="fd-properties-section">
          <div className="fd-properties-section-title">Table</div>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Label size="sm">Rows</Label>
                <Input
                  type="number"
                  bsSize="sm"
                  min={1}
                  value={el.typeProps?.table?.rows || 1}
                  onChange={(e) => {
                    const rows = Math.max(1, Number(e.target.value));
                    const t = el.typeProps?.table!;
                    const cells = Array.from({ length: rows }, (_, r) => t.cells[r] || Array.from({ length: t.cols }, () => []));
                    const rowHeights = Array.from({ length: rows }, (_, r) => t.rowHeights[r] || 40);
                    applyTable({ ...t, rows, cells, rowHeights });
                  }}
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Label size="sm">Columns</Label>
                <Input
                  type="number"
                  bsSize="sm"
                  min={1}
                  value={el.typeProps?.table?.cols || 1}
                  onChange={(e) => {
                    const cols = Math.max(1, Number(e.target.value));
                    const t = el.typeProps?.table!;
                    const cells = t.cells.map((row) => Array.from({ length: cols }, (_, c) => row[c] || []));
                    const colWidths = Array.from({ length: cols }, () => 100 / cols);
                    applyTable({ ...t, cols, cells, colWidths, merges: [] });
                  }}
                />
              </FormGroup>
            </Col>
          </Row>
          <div className="fd-btn-row mt-1">
            <Button size="sm" color="light" onClick={addTableRow}>+ Row</Button>
            <Button size="sm" color="light" outline onClick={removeTableRow}>&minus; Row</Button>
            <Button size="sm" color="light" onClick={addTableColumn}>+ Column</Button>
            <Button size="sm" color="light" outline onClick={removeTableColumn}>&minus; Column</Button>
          </div>
          {cellEditor}
        </div>
      )}

      {el.type === 'image' && (
        <div className="fd-properties-section">
          <div className="fd-properties-section-title">Image</div>
          <Input
            type="file"
            bsSize="sm"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                updateTypeProps({ image: { ...el.typeProps?.image, src: String(reader.result) } });
              };
              reader.onerror = () => {
                Swal.fire({ title: 'Insurance Verification Platform', text: 'Could not read that image file.', icon: 'warning' });
              };
              reader.readAsDataURL(file);
            }}
          />
          <FormGroup className="mt-2 mb-0">
            <Label size="sm">Fit</Label>
            <Input
              type="select"
              bsSize="sm"
              value={el.typeProps?.image?.fit || 'contain'}
              onChange={(e) => updateTypeProps({ image: { ...el.typeProps?.image, fit: e.target.value as any } })}
            >
              <option value="contain">Contain</option>
              <option value="cover">Cover</option>
              <option value="fill">Fill</option>
            </Input>
          </FormGroup>
        </div>
      )}

      {BINDABLE_TYPES.has(el.type) && (
        <div className="fd-properties-section">
          <div className="fd-properties-section-title">Binding</div>
          <FormGroup>
            <Label size="sm">Default value</Label>
            <Input bsSize="sm" value={el.bindings?.defaultValue || ''} onChange={(e) => updateBindings({ defaultValue: e.target.value })} />
          </FormGroup>
          <FormGroup check>
            <Label check>
              <Input type="checkbox" checked={!!el.bindings?.required} onChange={(e) => updateBindings({ required: e.target.checked })} />
              {' '}Required
            </Label>
          </FormGroup>
          <FormGroup check>
            <Label check>
              <Input type="checkbox" checked={!!el.bindings?.readOnly} onChange={(e) => updateBindings({ readOnly: e.target.checked })} />
              {' '}Read only
            </Label>
          </FormGroup>
        </div>
      )}
    </div>
  );
}
